import type { Order } from "@estelle/domain";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { reportingNow } from "@/lib/reportClock";
import { cn } from "@/lib/utils";
import { CommentBody } from "@/modules/comments/CommentBody";
import { commentKeys } from "@/modules/comments/keys";
import { timeAgo } from "@/modules/orders/tracking";

/**
 * The comments icon beside the order number (#623, #643): shown only on an
 * order that has comments, with how many, and opening them without opening
 * the order. The count comes with the list row (`commentCount`); the comments
 * themselves are fetched when it opens, under the thread's own query key, so
 * opening the order afterwards reads them from the cache.
 */
export function OrderCommentsQuickView({
  order,
  className,
}: {
  order: Pick<Order, "id" | "orderNumber" | "commentCount">;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (order.commentCount === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          aria-label={`${String(order.commentCount)} ${order.commentCount === 1 ? "comment" : "comments"} on ${order.orderNumber}`}
          onClick={(event) => {
            event.stopPropagation();
          }}
          className={cn(
            "text-tint-blue-foreground h-6 shrink-0 gap-0.5 px-1 text-xs tabular-nums pointer-coarse:h-9 pointer-coarse:px-2",
            className,
          )}
        >
          <MessageSquare className="size-3.5" aria-hidden />
          {order.commentCount}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={16}
        className="max-h-[min(24rem,var(--radix-popover-content-available-height))] w-80 max-w-[calc(100vw-2rem)] overflow-y-auto p-0"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {open ? (
          <CommentsBody orderId={order.id} title={order.orderNumber} />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

function CommentsBody({ orderId, title }: { orderId: string; title: string }) {
  const data = useDataSource();
  const query = useQuery({
    queryKey: commentKeys.thread("order", orderId),
    queryFn: () => data.comments.list("order", orderId),
  });
  const comments = (query.data?.items ?? []).filter(
    (comment) => comment.deletedAt === null,
  );

  return (
    <div className="px-4 py-3">
      <p className="text-sm font-medium">Comments on {title}</p>
      {query.isPending ? (
        <div className="mt-3 space-y-2" aria-hidden>
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-3/5" />
        </div>
      ) : query.isError ? (
        <p className="text-destructive mt-2 text-sm">
          Could not load the comments.
        </p>
      ) : (
        <ul className="mt-2 divide-y">
          {comments.map((comment) => (
            <li key={comment.id} className="py-2">
              <p className="text-muted-foreground text-xs">
                <span className="text-foreground font-medium">
                  {comment.authorName}
                </span>{" "}
                · {timeAgo(comment.createdAt, reportingNow())}
              </p>
              {comment.body !== "" ? (
                <CommentBody
                  body={comment.body}
                  mentions={comment.mentions}
                  className="mt-0.5 text-sm"
                />
              ) : null}
              {comment.attachments.length > 0 ? (
                <p className="text-muted-foreground mt-0.5 text-xs">
                  {comment.attachments.length} file
                  {comment.attachments.length === 1 ? "" : "s"} attached
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
